// Shared payload builder for /api/client_error.
//
// Why a watcher: by the time global-error.tsx reports, React has replaced the
// whole <html> tree with the error UI, so <html lang>/class and any <font>
// nodes added by browser translation are already gone. startErrorDiagnostics()
// runs from the layout and records those signals *before* a crash, plus the
// last user interactions, so the report can say what happened.

export type Breadcrumb = {
    t: number // ms since diagnostics started
    type: string
    target: string
    path: string
}

type Watcher = {
    startedAt: number
    initialLang: string
    lastLang: string
    langChanges: string[]
    htmlClassSeen: string
    translatedSeen: boolean
    sawFontNode: boolean
    breadcrumbs: Breadcrumb[]
}

const MAX_BREADCRUMBS = 10
const TRANSLATED_CLASS_RE = /\btranslated-(ltr|rtl)\b/

let watcher: Watcher | null = null

function describeTarget(el: EventTarget | null): string {
    if (!(el instanceof Element)) return ''
    const qtyAction = el.getAttribute('data-qty-action')
    const cls = (el.getAttribute('class') || '').trim().split(/\s+/).slice(0, 3).join('.')
    const link = el.closest('a[href]')?.getAttribute('href')
    const alt = el.tagName === 'IMG' ? (el.getAttribute('alt') || '').slice(0, 30) : ''
    return (
        `${el.tagName.toLowerCase()}${cls ? '.' + cls : ''}${qtyAction ? `[qty:${qtyAction}]` : ''}` +
        `${link ? ` → ${link.slice(0, 60)}` : ''}${alt ? ` alt="${alt}"` : ''}`
    )
}


// ---------------------------------------------------------------------------
// DOM failure probe (diagnostic only, remove once the culprit is found).
// Wraps Node.prototype.removeChild / insertBefore, records WHICH node failed
// and where it actually is, then rethrows the original error unchanged.
// ---------------------------------------------------------------------------

export type DomFailure = {
    op: string
    parent: string
    child: string
    childRealParent: string
    childParentIsFont: boolean
    parentInNoTranslate: boolean
    parentChain: string
}

let lastDomFailure: DomFailure | null = null
let probeInstalled = false

function describeNode(n: Node | null | undefined): string {
    if (!n) return 'null'
    if (n.nodeType === Node.TEXT_NODE) {
        return `#text "${(n.nodeValue || '').replace(/\s+/g, ' ').trim().slice(0, 40)}"`
    }
    if (!(n instanceof Element)) return n.nodeName
    const id = n.id ? `#${n.id}` : ''
    const cls = (n.getAttribute('class') || '').trim().split(/\s+/).filter(Boolean).slice(0, 3).join('.')
    return `${n.tagName.toLowerCase()}${id}${cls ? '.' + cls : ''}`
}

function describeChain(n: Node | null): string {
    const parts: string[] = []
    let cur: Node | null = n
    for (let i = 0; cur && i < 6; i++) {
        parts.push(describeNode(cur))
        cur = cur.parentNode
    }
    return parts.join(' < ')
}

function recordFailure(op: string, parent: Node, child: Node | null) {
    try {
        const real = child ? child.parentNode : null
        lastDomFailure = {
            op,
            parent: describeNode(parent),
            child: describeNode(child),
            childRealParent: describeNode(real),
            childParentIsFont: real?.nodeName === 'FONT',
            parentInNoTranslate: parent instanceof Element && !!parent.closest('[translate="no"]'),
            parentChain: describeChain(parent),
        }
    } catch {
        // diagnostics must never throw
    }
}

export function installDomFailureProbe() {
    if (probeInstalled || typeof window === 'undefined') return
    probeInstalled = true

    const origRemove = Node.prototype.removeChild
    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
        try {
            return origRemove.call(this, child) as T
        } catch (e) {
            if (e instanceof DOMException && e.name === 'NotFoundError') recordFailure('removeChild', this, child)
            throw e
        }
    }

    const origInsert = Node.prototype.insertBefore
    Node.prototype.insertBefore = function <T extends Node>(this: Node, node: T, ref: Node | null): T {
        try {
            return origInsert.call(this, node, ref) as T
        } catch (e) {
            if (e instanceof DOMException && e.name === 'NotFoundError') recordFailure('insertBefore(ref)', this, ref)
            throw e
        }
    }
}

export function startErrorDiagnostics() {
    if (watcher || typeof window === 'undefined') return

    const html = document.documentElement
    const w: Watcher = {
        startedAt: Date.now(),
        initialLang: html.getAttribute('lang') || '',
        lastLang: html.getAttribute('lang') || '',
        langChanges: [],
        htmlClassSeen: html.className || '',
        translatedSeen: TRANSLATED_CLASS_RE.test(html.className || ''),
        sawFontNode: false,
        breadcrumbs: [],
    }
    watcher = w

    // Chrome/Google Translate: toggles html class and rewrites lang.
    new MutationObserver(() => {
        const cls = html.className || ''
        const lang = html.getAttribute('lang') || ''
        if (TRANSLATED_CLASS_RE.test(cls)) w.translatedSeen = true
        if (cls !== w.htmlClassSeen) w.htmlClassSeen = cls
        if (lang !== w.lastLang) {
            w.langChanges.push(`${w.lastLang || '-'}→${lang || '-'}@${Date.now() - w.startedAt}ms`)
            w.langChanges = w.langChanges.slice(-5)
            w.lastLang = lang
        }
    }).observe(html, { attributes: true, attributeFilter: ['class', 'lang'] })

    // Google Translate wraps text nodes in <font>; React never creates <font>.
    new MutationObserver((records) => {
        if (w.sawFontNode) return
        for (const r of records) {
            for (const n of Array.from(r.addedNodes)) {
                if (n.nodeName === 'FONT') {
                    w.sawFontNode = true
                    return
                }
            }
        }
    }).observe(document.body, { childList: true, subtree: true })

    // Last few user interactions (no text content, only tag/class).
    const push = (type: string) => (e: Event) => {
        w.breadcrumbs.push({
            t: Date.now() - w.startedAt,
            type,
            target: describeTarget(e.target),
            path: window.location.pathname,
        })
        if (w.breadcrumbs.length > MAX_BREADCRUMBS) w.breadcrumbs.shift()
    }
    document.addEventListener('click', push('click'), true)
    document.addEventListener('change', push('change'), true)
    window.addEventListener('cartUpdate', push('cartUpdate'))
}

export function collectTranslationInfo() {
    const html = document.documentElement
    const htmlClass = html.className || ''
    const htmlLang = html.getAttribute('lang') || ''
    const urlLocale = window.location.pathname.split('/')[1] || ''

    const googtransCookie =
        document.cookie.split('; ').find((c) => c.startsWith('googtrans=')) || ''
    const fontNodes = document.querySelectorAll('font').length
    const msTranslatorNodes = document.querySelectorAll('[_msttexthash]').length

    const w = watcher
    const live =
        TRANSLATED_CLASS_RE.test(htmlClass) ||
        !!googtransCookie ||
        fontNodes > 0 ||
        msTranslatorNodes > 0

    return {
        // true if translation was detected now OR at any point before the crash
        translated: live || !!w?.translatedSeen || !!w?.sawFontNode || (w?.langChanges.length ?? 0) > 0,
        watcherStarted: !!w,
        // live values (usually wiped inside global-error)
        htmlClass,
        htmlLang,
        urlLocale,
        googtransCookie,
        fontNodes,
        msTranslatorNodes,
        // values recorded before the crash
        initialLang: w?.initialLang ?? '',
        lastLang: w?.lastLang ?? '',
        langChanges: w?.langChanges ?? [],
        htmlClassSeen: w?.htmlClassSeen ?? '',
        translatedSeen: !!w?.translatedSeen,
        sawFontNode: !!w?.sawFontNode,
        browserLang: navigator.language || '',
        uptimeMs: w ? Date.now() - w.startedAt : null,
    }
}

export type TranslationInfo = ReturnType<typeof collectTranslationInfo>

export function buildClientErrorPayload(
    error: Error & { digest?: string },
    prefix = '',
    extra: { autoReload?: boolean } = {}
) {
    let translation: TranslationInfo | null = null
    try {
        translation = collectTranslationInfo()
    } catch {
        // never let diagnostics break error reporting
    }

    return {
        timestamp: new Date().toISOString(),
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        errorName: error.name,
        errorMessage: `${prefix}${error.message}`,
        errorStack: error.stack,
        errorDigest: error.digest,
        url: window.location.href,
        page: window.location.pathname,
        translation,
        breadcrumbs: watcher?.breadcrumbs ?? [],
        autoReload: !!extra.autoReload,
        domFailure: lastDomFailure,
    }
}
